import React, { useMemo, useState } from 'react';
import { MoreHorizontalIcon } from 'lucide-react';
import { Pagination, Table, TableSkeleton, TD, TH, THead, TR } from '../ui/Table';
import { SearchInput } from '../ui/SearchInput';
import { Select } from '../ui/Select';
import { MultiSelect } from '../ui/MultiSelect';
import { DevicePanel } from '../panels/DevicePanel';
import { deviceTypeOptions, planLabel, schoolFilterOptions, schoolName } from '../../data/district';
import { devices } from '../../data/devices';
import { studentById } from '../../data/students';
import { usePagination } from '../../hooks/usePagination';
import { Device } from '../../types';
const typeOptions = deviceTypeOptions.filter(t => t !== 'All Device Types') as string[];
export function DevicesTab({
  program,
  loading
}: {
  program: 'optin' | 'group';
  loading: boolean;
}) {
  const [query, setQuery] = useState('');
  const [school, setSchool] = useState('All Schools');
  const [types, setTypes] = useState<string[]>([]);
  const [selected, setSelected] = useState<Device | null>(null);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter(device => {
      if (device.program !== program) return false;
      if (program === 'optin' && school !== 'All Schools' && schoolName(device.schoolSlug) !== school) {
        return false;
      }
      if (types.length > 0 && !types.includes(device.deviceType)) return false;
      if (!q) return true;
      return `${device.serialNumber} ${planLabel(device.planId)} ${device.assetTagId}`.toLowerCase().includes(q);
    });
  }, [query, school, types, program]);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(rows, 15);
  return <div className="flex flex-1 flex-col" data-id="element-129" data-mp="30m82d8n4d2wx">
      <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-130" data-mp="243ijhv5idelu">
        {program === 'optin' && <Select options={schoolFilterOptions} value={school} placeholder="All Schools" onChange={setSchool} data-id="element-131" data-mp="28543s1kdgv62" />}
        <MultiSelect options={typeOptions} allLabel="All Device Types" selected={types} onChange={setTypes} data-id="element-132" data-mp="3bfhusdd2bwkm" />
        <SearchInput value={query} onChange={setQuery} placeholder="Search by device serial number or plans" className="w-[260px]" data-id="element-133" data-mp="36hq57pzw4mjl" />
      </div>

      <Table minWidth={1380} data-id="element-134" data-mp="3g6hsvlxx4kop">
        <THead data-id="element-135" data-mp="3g79qf2b2sshm">
          <tr data-id="element-136" data-mp="3g8ukkqtha9ey">
            <TH data-id="element-137" data-mp="3g941lowjp68i">Device Type</TH>
            <TH data-id="element-138" data-mp="2sgdpnfon6my8">Serial Number</TH>
            <TH data-id="element-139" data-mp="2shp3k8bsxb32">Make</TH>
            <TH data-id="element-140" data-mp="2sj09adot4swt">Model</TH>
            <TH data-id="element-141" data-mp="2nicah5uc77s1">Associated Plan</TH>
            <TH data-id="element-142" data-mp="2njdtpxlib0ku">School</TH>
            <TH data-id="element-143" data-mp="2nk6v0j1p4xvl">Student Name</TH>
            <TH data-id="element-144" data-mp="31hlqa4emrzvh">Asset Tag ID</TH>
            <TH width={56} data-id="element-145" data-mp="31imzs4g00eb3" />
          </tr>
        </THead>
        {loading ? <TableSkeleton columns={9} data-id="element-146" data-mp="2xg0e6l9x7pmc" /> : <tbody data-id="element-147" data-mp="2xicvqxbrnxyv">
            {pageItems.length === 0 && <TR data-id="element-148" data-mp="gqbexw81yl2a">
                <TD colSpan={9} className="py-10 text-center text-ink-500" data-id="element-149" data-mp="gpta4b7bqyir">
                  No devices found
                </TD>
              </TR>}
            {pageItems.map(device => {
          const student = studentById(device.studentId);
          return <TR key={device.id} onClick={() => setSelected(device)} data-id="element-150" data-mp="pa7yyky4ofbv">
                  <TD data-id="element-151" data-mp="ac5rhznz79pc">{device.deviceType}</TD>
                  <TD data-id="element-152" data-mp="adh0qf06x9nu">{device.serialNumber}</TD>
                  <TD data-id="element-153" data-mp="f9nw5duyn2rj">{device.make}</TD>
                  <TD data-id="element-154" data-mp="faphqc9lr7ni">{device.model}</TD>
                  <TD data-id="element-155" data-mp="130bdepqrn4qr">{planLabel(device.planId)}</TD>
                  <TD data-id="element-156" data-mp="130tkyiivp5dz">{device.schoolSlug ? schoolName(device.schoolSlug) : '-'}</TD>
                  <TD data-id="element-157" data-mp="17xtig51nwzth">{student ? student.name : '-'}</TD>
                  <TD data-id="element-158" data-mp="tai3dt69uxom">{device.assetTagId}</TD>
                  <TD data-id="element-159" data-mp="t9p6djx47mjo">
                    <button type="button" aria-label={`Device actions for ${device.serialNumber}`} onClick={e => {
                e.stopPropagation();
                setSelected(device);
              }} className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-500 hover:bg-ink-100 hover:text-ink-700" data-id="element-160" data-mp="t85pps7jr9hd">
                      <MoreHorizontalIcon className="h-4 w-4" data-id="element-161" data-mp="3u6kk16oty6my" />
                    </button>
                  </TD>
                </TR>;
        })}
          </tbody>}
      </Table>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-162" data-mp="3kaptlped779m" />

      <DevicePanel device={selected} onClose={() => setSelected(null)} data-id="element-163" data-mp="lsby2hgtce3r" />
    </div>;
}